import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { findCommunityContent, type CommunityContent } from "@/lib/communities";
import { useSeo } from "@/hooks/useSeo";
import { useJsonLd } from "@/hooks/useJsonLd";
import { absoluteUrl, breadcrumbLd } from "@/lib/structured-data";
import { api } from "@/lib/api";
import type { Property } from "@/types/property";
import { PropertyCard } from "@/components/site/PropertyCard";
import { SafeImage } from "@/components/site/SafeImage";
import { useLiveProperties } from "@/hooks/useLiveProperties";

export function CommunityPage() {
  const { slug = "" } = useParams();
  const content = findCommunityContent(slug);

  // Render the view component only when we have valid content so the
  // hooks inside CommunityView don't run for a missing community.
  if (!content) {
    return <Navigate to="/areas" replace />;
  }

  // Use the slug as a stable key so React remounts CommunityView when
  // the user navigates between community pages, resetting all internal
  // state (in-flight requests, scroll position, etc.).
  return <CommunityView key={content.slug} content={content} />;
}

function CommunityView({ content }: { content: CommunityContent }) {
  const { version } = useLiveProperties();
  const [items, setItems] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: content.seoTitle,
    description: content.seoDescription,
    // The curated community cover doubles as the OG image so social
    // previews land on something Dubai-specific instead of the brand
    // logo fallback.
    ogImage: absoluteUrl(content.fallbackCover),
  });

  useJsonLd(
    "community-breadcrumb",
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Areas", path: "/areas" },
      { name: content.name, path: `/areas/${content.slug}` },
    ]),
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .listProperties({
        community: content.name,
        pageSize: 9,
        sort: "newest",
      })
      .then((data) => !cancelled && setItems(data.items))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [content.name, version]);

  const propertyCount = items.length;

  return (
    <>
      {/* Hero — large cover photo + headline */}
      <section className="relative isolate overflow-hidden bg-[var(--navy-deep)]" data-on-dark>
        <div className="absolute inset-0">
          <SafeImage
            src={content.fallbackCover}
            alt={`${content.name}, Dubai`}
            className="h-full w-full object-cover"
            fetchPriority="high"
          />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(13,20,34,0.78) 0%, rgba(13,20,34,0.45) 35%, rgba(13,20,34,0.55) 65%, rgba(13,20,34,0.92) 100%)",
            }}
          />
        </div>

        <div className="relative container-wide pt-[14vh] pb-12 sm:pt-[18vh] md:pt-[20vh] md:pb-16 short:pt-14 short:pb-8">
          <Link
            to="/areas"
            className="tap-target inline-flex items-center gap-2 text-[11.5px] uppercase tracking-[0.22em] text-[var(--text-on-dark-soft)] hover:text-[var(--cream)]"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M12 7H2m4-4-4 4 4 4" stroke="currentColor" strokeWidth="1.4" />
            </svg>
            All Communities
          </Link>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <p className="text-[16px] text-[var(--cream)] sm:text-[18px]">{`${content.name}, Dubai`}</p>
            <span className="inline-flex border border-[rgba(195,158,68,0.55)] px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-bright)]">
              {content.highlight}
            </span>
          </div>
          <h1 className="mt-3 display-xl max-w-[22ch] text-[var(--text-on-dark)] sm:mt-4">
            {content.pageHeadline}
          </h1>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <Link
              to={`/properties?community=${encodeURIComponent(content.name)}`}
              className="btn btn-primary btn-block-sm"
            >
              See Available Properties
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7h10m-4-4 4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.4"
                />
              </svg>
            </Link>
            <Link to="/contact" className="btn btn-ghost btn-block-sm">
              {content.ctaLabel}
            </Link>
          </div>
        </div>
      </section>

      {/* The community story — sits below the image so the hero stays
          clean: just the title and the calls to action. */}
      <section className="border-b border-[var(--line)]">
        <div className="container-wide py-10 md:py-14 short:py-8">
          <p className="eyebrow">About {content.name}</p>
          <div className="mt-4 max-w-[68ch] space-y-4 text-[15px] leading-[1.7] text-[var(--text-soft)] md:text-[16px]">
            {content.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="border-b border-[var(--line)] bg-[var(--bg-2)]">
        <div className="container-wide grid gap-3 py-7 text-[13.5px] sm:grid-cols-3 sm:py-9">
          <Stat
            label="Properties Available"
            value={
              loading ? "—" : String(propertyCount).padStart(2, "0")
            }
          />
          {content.statsBar?.propertyTypes && (
            <Stat label="Types" value={content.statsBar.propertyTypes} />
          )}
          {content.statsBar?.avgYield && (
            <Stat label="Avg Rental Yield" value={content.statsBar.avgYield} />
          )}
        </div>
      </section>

      {/* Properties */}
      <section className="container-wide py-12 md:py-20 short:py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Available Now</p>
            <h2 className="mt-3 display-lg max-w-[24ch]">{`Properties in ${content.name}.`}</h2>
          </div>
          <Link
            to={`/properties?community=${encodeURIComponent(content.name)}`}
            className="link-arrow"
          >
            See All Listings
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 7h10m-4-4 4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.2"
              />
            </svg>
          </Link>
        </div>

        {loading && (
          <div className="mt-10 grid gap-x-6 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-4">
                <div className="aspect-[4/5] animate-pulse bg-[var(--surface-2)]" />
                <div className="h-3 w-1/3 animate-pulse bg-[var(--surface-2)]" />
                <div className="h-5 w-2/3 animate-pulse bg-[var(--surface-2)]" />
              </div>
            ))}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="mt-10 grid gap-x-6 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <PropertyCard key={p.id} property={p} />
            ))}
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="mt-10 border border-dashed border-[var(--line-strong)] p-10 text-center">
            <p
              className="font-display text-[24px] text-[var(--text)] sm:text-[28px]"
              style={{ fontVariationSettings: '"opsz" 36' }}
            >
              No live listings in {content.name} right now.
            </p>
            <p className="mt-3 text-[14.5px] text-[var(--text-soft)]">
              Tell us your brief and we'll surface what's coming to market in
              this community before it goes public.
            </p>
            <Link to="/contact" className="btn btn-ghost mt-6">
              {content.ctaLabel}
            </Link>
          </div>
        )}
      </section>

    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="eyebrow text-[var(--text-muted)]">{label}</p>
      <p className="text-[18px] text-[var(--text)] sm:text-[20px]">{value}</p>
    </div>
  );
}
